import React, { useEffect, useRef, useState } from 'react';
import { Save, Power, Loader2, Upload, Camera, MapPin, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import api, { vendorAdminApi } from '../services/api';
import { useVendorContext } from './VendorAdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const StorePage = () => {
  const { vendor_id, impersonating } = useVendorContext();
  const [store, setStore] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, cover: false });
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await vendorAdminApi.store(impersonating ? vendor_id : undefined);
        setStore(s);
        setForm({
          name: s.name || '', cuisine: s.cuisine || '', deliveryTime: s.deliveryTime || '',
          deliveryFee: s.deliveryFee ?? 0, minOrder: s.minOrder ?? 0, distance: s.distance || '',
          cover: s.cover || '', logo: s.logo || '', promo: s.promo || '',
          tags: (s.tags || []).join(', '), is_active: s.is_active !== false,
          country: s.country || '', state: s.state || '', city: s.city || '',
        });
      } catch { toast.error('Failed to load store'); }
    })();
  }, [vendor_id, impersonating]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        deliveryFee: Number(form.deliveryFee),
        minOrder: Number(form.minOrder),
        promo: form.promo || null,
        tags: form.tags.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const updated = await vendorAdminApi.updateStore(payload, impersonating ? vendor_id : undefined);
      setStore(updated);
      toast.success('Store updated');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async () => {
    try {
      const updated = await vendorAdminApi.updateStore({ is_active: !(store?.is_active !== false) }, impersonating ? vendor_id : undefined);
      setStore(updated);
      setForm((f) => ({ ...f, is_active: updated.is_active !== false }));
      toast.success(updated.is_active === false ? 'Store closed' : 'Store is open');
    } catch (err) { toast.error('Failed'); }
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error('Please pick an image'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Image is too large (max 8 MB)'); return; }
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = data.url;
      // Save immediately so it survives without needing the customer to click "Save changes".
      const updated = await vendorAdminApi.updateStore({ [kind]: url }, impersonating ? vendor_id : undefined);
      setStore(updated);
      setForm((f) => ({ ...f, [kind]: url }));
      toast.success(`${kind === 'logo' ? 'Logo' : 'Cover photo'} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const clearImage = async (kind) => {
    try {
      const updated = await vendorAdminApi.updateStore({ [kind]: '' }, impersonating ? vendor_id : undefined);
      setStore(updated);
      setForm((f) => ({ ...f, [kind]: '' }));
      toast.success(`${kind === 'logo' ? 'Logo' : 'Cover photo'} removed`);
    } catch (err) { toast.error('Failed'); }
  };

  if (!form) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Store settings</h1>
          <p className="text-slate-500 mt-1">Edit how your store appears to customers.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={toggleActive} className={form.is_active ? 'text-rose-600 hover:text-rose-700 border-rose-200' : ''}>
            <Power className="h-4 w-4 mr-1.5" />{form.is_active ? 'Close store' : 'Open store'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Cuisine / type" value={form.cuisine} onChange={(v) => setForm({ ...form, cuisine: v })} />
            <Field label="Delivery time" value={form.deliveryTime} onChange={(v) => setForm({ ...form, deliveryTime: v })} placeholder="20–30 min" />
            <Field label="Distance" value={form.distance} onChange={(v) => setForm({ ...form, distance: v })} placeholder="1.2 km" />
            <Field label="Delivery fee ($)" type="number" value={form.deliveryFee} onChange={(v) => setForm({ ...form, deliveryFee: v })} />
            <Field label="Min order ($)" type="number" value={form.minOrder} onChange={(v) => setForm({ ...form, minOrder: v })} />
            <Field label="Promo label (optional)" value={form.promo} onChange={(v) => setForm({ ...form, promo: v })} placeholder="20% off" />
            <Field label="Tags (comma-sep)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="Pizza, Italian" />
          </div>
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Location (used for order numbers)</div>
                <div className="text-[11px] text-slate-400">Update the exact pickup point for drivers under Settings → Store location.</div>
              </div>
              <Link
                to={`/vendor-admin/settings${impersonating ? `?vendor_id=${vendor_id}` : ''}#store-location-panel`}
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                data-testid="store-location-link"
              >
                <MapPin className="h-3.5 w-3.5" /> Update precise location →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} placeholder="Canada" />
              <Field label="State / Province" value={form.state} onChange={(v) => setForm({ ...form, state: v })} placeholder="Ontario" />
              <Field label="Town / City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} placeholder="Ottawa" />
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Your future orders will look like <code className="font-mono">{((form.country || 'XXX').toUpperCase().replace(/[^A-Z]/g, '') + 'XXX').slice(0, 3)}-{((form.state || 'XXX').toUpperCase().replace(/[^A-Z]/g, '') + 'XXX').slice(0, 3)}-{((form.city || 'XXX').toUpperCase().replace(/[^A-Z]/g, '') + 'XXX').slice(0, 3)}-{String(store?.vendor_number || 0).padStart(3, '0')}-XXXXX</code></p>
          </div>
          <div className="pt-2 border-t border-slate-100 space-y-4">
            <div>
              <Label className="text-xs text-slate-600 uppercase tracking-wider">Cover photo</Label>
              <p className="text-[11px] text-slate-400 mt-0.5">Wide banner shown at the top of your storefront. Recommended 1600×900.</p>
              <ImagePickerRow
                testId="store-cover"
                value={form.cover}
                busy={uploading.cover}
                inputRef={coverInputRef}
                onPick={(f) => uploadImage('cover', f)}
                onClear={() => clearImage('cover')}
                previewClass="h-40 w-full rounded-xl object-cover border border-slate-200"
                placeholder="No cover photo yet"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 uppercase tracking-wider">Logo</Label>
              <p className="text-[11px] text-slate-400 mt-0.5">Square logo shown next to your store name and in orders. Recommended 512×512.</p>
              <ImagePickerRow
                testId="store-logo"
                value={form.logo}
                busy={uploading.logo}
                inputRef={logoInputRef}
                onPick={(f) => uploadImage('logo', f)}
                onClear={() => clearImage('logo')}
                previewClass="h-24 w-24 rounded-xl object-cover border border-slate-200"
                placeholder="No logo yet"
              />
            </div>
          </div>
        </div>

        <aside className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="relative h-40 bg-slate-100">
            {form.cover && <img src={form.cover} alt={form.name} className="w-full h-full object-cover" />}
            {!form.is_active && (
              <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center text-white text-sm font-medium">Closed</div>
            )}
          </div>
          <div className="p-4">
            <div className="font-semibold text-slate-900">{form.name || 'Your store'}</div>
            <div className="text-xs text-slate-500 mt-0.5">{form.cuisine || 'Cuisine'} · {form.distance}</div>
            <div className="text-xs text-slate-500 mt-2">{form.deliveryTime} · ${Number(form.deliveryFee || 0).toFixed(2)} delivery</div>
            {form.promo && <div className="mt-3 inline-block px-2 py-1 rounded-md bg-emerald-600 text-white text-xs font-medium">{form.promo}</div>}
          </div>
        </aside>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 h-10" />
  </div>
);

const ImagePickerRow = ({ testId, value, busy, inputRef, onPick, onClear, previewClass, placeholder }) => (
  <div className="mt-2 flex items-center gap-3 flex-wrap" data-testid={`${testId}-row`}>
    {value ? (
      <img src={value} alt="preview" className={previewClass} data-testid={`${testId}-preview`} />
    ) : (
      <div className={`${previewClass} bg-slate-50 border-dashed flex items-center justify-center text-[11px] text-slate-400`} data-testid={`${testId}-empty`}>
        {placeholder}
      </div>
    )}
    <div className="flex flex-col gap-1.5">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onPick(f); }}
        data-testid={`${testId}-file-input`}
      />
      <input
        ref={(el) => { if (inputRef && el) inputRef.cameraEl = el; }}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onPick(f); }}
        data-testid={`${testId}-camera-input`}
        id={`${testId}-camera-input`}
      />
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => document.getElementById(`${testId}-camera-input`)?.click()} data-testid={`${testId}-take-photo-btn`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Camera className="h-3.5 w-3.5 mr-1.5" />} Take photo
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testId}-choose-file-btn`}>
          <Upload className="h-3.5 w-3.5 mr-1.5" /> Choose file
        </Button>
        {value && (
          <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={onClear} disabled={busy} data-testid={`${testId}-clear-btn`}>
            <X className="h-3.5 w-3.5 mr-1" /> Remove
          </Button>
        )}
      </div>
      <span className="text-[11px] text-slate-400">Upload from your phone, camera, or computer. Max 8 MB.</span>
    </div>
  </div>
);

export default StorePage;
